import builder from "@rsql/builder";
import { emit } from "@rsql/emitter";
import {
  AdvancedFilterDateRangeValue,
  AdvancedFilterMultiselectValue,
  AdvancedFilterRadioGroupValue,
  AdvancedFilterToggleValue,
  AdvancedFiltersValues,
} from "../types/filters";
import { ARCHIVED_STATUS_CODE, UNPUBLISHED_STATUS_CODE } from "../store/filters/view-toggles";
import { formatDateTimeAsUTCRegardlessOfTimezone } from "@/utils/date-utils";

/** Toggle key → the status it excludes while off. These are view-only; they have no `keyMap` entry. */
const HIDE_STATUS_TOGGLES: Record<string, { attribute: string; statusCode: string }> = {
  showArchived: { attribute: "status_code", statusCode: ARCHIVED_STATUS_CODE },
  showUnpublished: { attribute: "global_status", statusCode: UNPUBLISHED_STATUS_CODE },
};

/**
 * Serialize the given filters in RSQL format.
 * @param appliedFilters the filters to be serialized in RSQL
 * @param keyMap the map between the filter keys expressed in the given appliedFilters and the attribute keys that the filters should be applied to
 * @returns a string representing the filters in RSQL format
 */
export function serializeToRsql(appliedFilters: AdvancedFiltersValues, keyMap: Record<string, string>) {
  const comparisonNodes = Object.entries(appliedFilters).flatMap(([appliedFilterKey, appliedFilter]) => {
    // View-only "hide by default" toggles have no `keyMap` entry; they emit an exclusion when off.
    const hideStatusToggle = HIDE_STATUS_TOGGLES[appliedFilterKey];
    if (hideStatusToggle && appliedFilter.type === "toggle") {
      return convertHideStatusToggle(appliedFilter, hideStatusToggle.attribute, hideStatusToggle.statusCode);
    }

    const attributeKey = keyMap[appliedFilterKey];

    if (!attributeKey) {
      throw new Error(`filtering not supported on '${appliedFilterKey}'`);
    }

    // Special handling for IN_REVIEW and PUBLISHED status in editorial filters
    if (appliedFilterKey === "calculated_status" && appliedFilter.type === "multiselect") {
      return convertEditorialStatusFilter(appliedFilter);
    }

    // Special handling for has images filter in editorial
    if (appliedFilterKey === "has_images" && appliedFilter.type === "radio-group") {
      return convertHasImagesFilter(attributeKey, appliedFilter);
    }

    switch (appliedFilter.type) {
      case "date-range":
        return convertDateRangeFilter(attributeKey, appliedFilter);
      case "multiselect":
        return convertMultiselectFilter(attributeKey, appliedFilter);
      case "radio-group":
        return convertRadioGroupFilter(attributeKey, appliedFilter);
    }

    // this unreachable return statement is to prevent SonarQube from complaining about inconsistent return style
    // that's confirmed to be a false positive in situations like this (a switch that cover each case)
    // https://community.sonarsource.com/t/typescript-s3801-false-positive-when-a-switch-handles-all-variants/91799
    return [];
  });

  if (comparisonNodes.length === 0) {
    return "";
  }

  const rootNode = builder.and(...comparisonNodes);
  return emit(rootNode);
}

/** Emits a single `<attribute>!=<statusCode>` exclusion when the toggle is off, nothing when on. */
function convertHideStatusToggle(filterValue: AdvancedFilterToggleValue, attributeKey: string, statusCode: string) {
  return filterValue.value ? [] : [builder.neq(attributeKey, statusCode)];
}

/**
 * Convert the given DateRange filter to one ComparisonNode per each restricted bound (both upper and lower bounds, upper bound only or lower bound only).
 * If the given DateRange filter is not constrained to any bound, then an empty array is returned because no filter needs to be applied.
 * @param attributeKey the key of the attribute against which to filter
 * @param filterValue the DateRange filter to apply to the given attributeKey
 * @returns an array of ComparisonNode
 */
function convertDateRangeFilter(attributeKey: string, filterValue: AdvancedFilterDateRangeValue) {
  const serializedFrom = formatDateTimeAsUTCRegardlessOfTimezone(filterValue.value.from, true);
  const serializedTo = formatDateTimeAsUTCRegardlessOfTimezone(filterValue.value.to, true);

  if (serializedFrom && serializedTo) {
    return [builder.ge(attributeKey, serializedFrom), builder.le(attributeKey, serializedTo)];
  } else if (serializedFrom) {
    return [builder.ge(attributeKey, serializedFrom)];
  } else if (serializedTo) {
    return [builder.le(attributeKey, serializedTo)];
  } else {
    return [];
  }
}

/**
 * Convert the given Multiselect filter to a ComparisonNode.
 * If the given Multiselect filter have no selected values than an empty array is returned because no filter needs to be applied.
 * @param attributeKey the key of the attribute against which to filter
 * @param filterValue the Multiselect filter to apply to the given attributeKey
 * @returns an array of ComparisonNode
 */
function convertMultiselectFilter(attributeKey: string, filterValue: AdvancedFilterMultiselectValue) {
  const values = filterValue.value.map((item) => item.value);
  return filterValue.value.length > 0 ? [builder.in(attributeKey, values)] : [];
}

/**
 * Convert the given Radio Group filter to a ComparisonNode.
 * If the given Radio Group filter has a null value, an empty array is returned because no filter needs to be applied.
 * @param attributeKey the key of the attribute against which to filter
 * @param filterValue the Radio Group filter to apply to the given attributeKey
 * @returns an array of ComparisonNode
 */
function convertRadioGroupFilter(attributeKey: string, filterValue: AdvancedFilterRadioGroupValue) {
  if (filterValue.value === null) {
    return [];
  }
  // Convert boolean to string for RSQL compatibility
  const value = typeof filterValue.value === "boolean" ? String(filterValue.value) : filterValue.value;

  return [builder.eq(attributeKey, value)];
}

function convertHasImagesFilter(attributeKey: string, filterValue: AdvancedFilterRadioGroupValue) {
  if (filterValue.value === null) {
    return [];
  }

  const exists = String(filterValue.value);

  return [builder.comparison(attributeKey, "=ex=", exists)];
}
/**
 * Convert the editorial status filter with special handling for IN_REVIEW and PUBLISHED statuses.
 * IN_REVIEW requires a complex nested query that checks for NOT_READY status with a distribution date.
 * PUBLISHED requires both experience and media statuses to be READY or SENT.
 * @param filterValue the Multiselect filter containing status values
 * @returns an array of ComparisonNode
 */
function convertEditorialStatusFilter(filterValue: AdvancedFilterMultiselectValue) {
  const selectedStatuses = filterValue.value.map((item) => item.value);

  if (selectedStatuses.length === 0) {
    return [];
  }

  const statusNodes = [];

  // Handle standard statuses (UNPUBLISHED, NOT_READY)
  const standardStatuses = selectedStatuses.filter((status) => status !== "IN_REVIEW" && status !== "PUBLISHED");
  if (standardStatuses.length > 0) {
    statusNodes.push(builder.in("global_status", standardStatuses));
  }

  // Handle PUBLISHED status with custom logic
  if (selectedStatuses.includes("PUBLISHED")) {
    // PUBLISHED means: (experienceStatus === "READY" || experienceStatus === "SENT")
    // AND (mediaStatus === "READY" || mediaStatus === "SENT")
    const publishedCondition = builder.and(
      builder.comparison(
        "experience_content",
        "=q=",
        "language_code==en;(experience_translation.distribution_status==READY,experience_translation.distribution_status==SENT)"
      ),
      builder.in("experience_media.distribution_status", ["READY", "SENT"])
    );
    statusNodes.push(publishedCondition);
  }

  // Handle IN_REVIEW status with custom nested query
  if (selectedStatuses.includes("IN_REVIEW")) {
    // IN_REVIEW means: (English content with NOT_READY translation status AND has distribution date)
    // OR (English content with NOT_READY media status AND has distribution date)
    const minDate = "1960-10-09T23:59:59.999Z";

    const translationNotReadyWithDate = builder.and(
      builder.comparison(
        "experience_content",
        "=q=",
        "language_code==en;experience_translation.distribution_status==NOT_READY"
      ),
      builder.gt("experience_media.distribution_date", minDate)
    );

    const mediaNotReadyWithDate = builder.and(
      builder.comparison("experience_content", "=q=", "language_code==en"),
      builder.in("experience_media.distribution_status", ["NOT_READY"]),
      builder.gt("experience_media.distribution_date", minDate)
    );

    statusNodes.push(builder.or(translationNotReadyWithDate, mediaNotReadyWithDate));
  }

  return statusNodes.length > 1 ? [builder.or(...statusNodes)] : statusNodes;
}
